import { useEventCallback } from '@mui/material';
import { Box } from '@mui/system';
import { setNestedObjectValues, useFormikContext } from 'formik';
import { CommonRequestCertificateInterface } from 'front-office/view-models/common-certificate-form';
import { useCallback, useMemo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { useNavigate } from 'react-router';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { PmiTooltipError } from 'shell/components/shared/tooltip-error';

interface PageButtonsProps {
  handleRequestCertificate: (values: CommonRequestCertificateInterface) => void;
  label?: string;
  isClaims?: boolean;
  hasCleanAccountholder?: boolean;
}

const PageButtons = ({
  handleRequestCertificate,
  label = 'Certificato',
  isClaims,
  hasCleanAccountholder = false,
}: PageButtonsProps): React.ReactElement => {
  const navigate = useNavigate();

  // handle the button on exit
  const handleExit = useCallback(() => {
    navigate('/sportello');
  }, [navigate]);

  const formContext = useFormikContext<CommonRequestCertificateInterface>();

  const isDisabledRequestCertificate = useMemo(() => {
    return (
      !!formContext.errors?.applicant ||
      !!formContext.errors?.generality ||
      !!formContext.errors?.accountholder ||
      !!formContext.errors.certifiableApplicant ||
      !!formContext.errors.certifiableAccountholder
    );
  }, [formContext.errors]);

  // Handle the submit
  const handleSubmit = useEventCallback(() => {
    formContext.validateForm(formContext.values).then((validationErrors) => {
      formContext.setTouched(setNestedObjectValues(validationErrors, true));

      if (Object.values(validationErrors).length > 0) {
        return;
      }

      handleRequestCertificate(formContext.values);
    });
  });

  const message = useMemo(() => {
    if (
      (formContext.errors.certifiableApplicant && formContext.touched.certifiableApplicant) ||
      (formContext.errors.certifiableAccountholder && formContext.touched.certifiableAccountholder)
    ) {
      return 'Il soggetto non è certificabile';
    }

    const isApplicantItalian = formContext.values?.applicant?.isItalian;

    const isAccountHolderItalian = formContext.values?.accountholder?.account?.applicant?.isItalian;
    if (isClaims) {
      if (formContext.values?.accountholder?.sameApplicant) {
        if (isApplicantItalian) {
          return 'Richiedente italiano';
        }
      } else {
        if (isAccountHolderItalian) {
          return 'Intestatario italiano';
        }
      }
    }

    if (formContext.values?.accountholder?.sameApplicant) {
      if (!formContext.values?.certifiableApplicant) {
        return 'Soggetto non certificabile';
      }
    } else {
      if (
        formContext?.values?.accountholder?.account?.applicant?.fiscalCode &&
        formContext?.values?.applicant?.fiscalCode
      ) {
        if (
          formContext.values?.accountholder?.account?.applicant?.fiscalCode ===
          formContext.values?.applicant?.fiscalCode
        ) {
          return `Richiedente è uguale all'intestatario`;
        }
      }

      if (!formContext.values?.accountholder?.certifiableAccountholder) {
        return 'Soggetto non certificabile';
      }
    }

    return 'Il form di ricerca non è valido';
  }, [formContext.errors, formContext.touched, formContext.values, isClaims]);

  return (
    <StickyDiv>
      <div>
        <div className="pmi-pb-4">
          <PmiTooltipError message={message} show={isDisabledRequestCertificate}>
            <div>
              <PmiButton variant="contained" color="cdmBrandThird" fullWidth onClick={handleSubmit}>
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Richiedi {label}
                </Box>
              </PmiButton>
            </div>
          </PmiTooltipError>
        </div>
        <div className="pmi-pb-2">
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => {
              formContext.handleReset();
              formContext.setFieldValue('isClearButtonClicked', true);
              formContext.validateForm();
            }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              PULISCI
            </Box>
          </PmiButton>
        </div>
        {hasCleanAccountholder ? (
          <div className="pmi-pb-2">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              onClick={() => {
                formContext.setValues({
                  ...formContext.values,
                  isClearButtonClicked: true,
                  accountholder: {
                    sameApplicant: formContext.values.accountholder?.sameApplicant,
                  },
                });
                formContext.validateForm();
              }}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                PULISCI SOLO INTESTATARIO
              </Box>
            </PmiButton>
          </div>
        ) : undefined}
      </div>

      <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={handleExit}>
        Esci
      </PmiButton>
    </StickyDiv>
  );
};

PageButtons.displayName = 'Page buttons';

export default PageButtons;
